import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fields = [
  { key: "company_name", label: "Nom de l'entreprise" },
  { key: "address", label: "Adresse / coordonnées" },
  { key: "phone", label: "Téléphone" },
  { key: "email", label: "E-mail" },
  { key: "logo_url", label: "URL du logo" },
  { key: "pagesjaunes_url", label: "Lien fiche PagesJaunes" },
  { key: "google_url", label: "Lien avis Google (laisser vide pour masquer)" },
  { key: "notification_recipients", label: "Destinataires des notifications" },
  { key: "primary_color", label: "Couleur d'action (hex)" },
];
const textareas = [
  { key: "landing_title", label: "Titre de la page publique" },
  { key: "landing_subtitle", label: "Sous-titre de la page publique" },
  { key: "whatsapp_message", label: "Message WhatsApp (utilisez {lien})" },
  { key: "sms_message", label: "Message SMS (utilisez {lien})" },
  { key: "legal_mentions", label: "Mentions légales" },
  { key: "privacy_policy", label: "Politique de confidentialité" },
  { key: "data_retention", label: "Durée de conservation des messages" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00e676] animate-spin" /></div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const inputCls = "w-full bg-[#0a0b0e] border border-[#262a33] rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:ring-2 focus:ring-[#00e676]";

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Paramètres</h1>
          <p className="text-[#64697a] mt-1">Gestion des avis – CC Auto 30</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center gap-2 bg-[#00e676] hover:bg-[#00c853] text-[#04120a] font-bold rounded-full px-5 py-2.5 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Enregistrer
        </button>
      </div>

      {s.logo_url && (
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-4 flex items-center gap-4">
          <img src={s.logo_url} alt="logo" className="w-16 h-16 object-contain" />
          <span className="text-sm text-[#64697a]">Aperçu du logo</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="text-sm text-[#a0a5b5] block mb-1.5">{f.label}</label>
            <input value={s[f.key] || ""} onChange={set(f.key)} className={inputCls} data-testid={`setting-${f.key}`} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {textareas.map((f) => (
          <div key={f.key}>
            <label className="text-sm text-[#a0a5b5] block mb-1.5">{f.label}</label>
            <textarea value={s[f.key] || ""} onChange={set(f.key)} className={inputCls + " min-h-[80px] resize-y"} data-testid={`setting-${f.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
